import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { BookOpen, X } from 'lucide-react';
import { TOPICS } from '../../data/onboarding';
import { helpForPage } from '../../data/pageHelp';
import { setDone } from '../../data/progress';
import { findTarget } from './targets';
import { guide, useGuide } from './guideStore';
import './GuideOverlay.css';

/* Icônes citées dans le texte d'une étape (marqueur `{icon}` + champ `icon`).
   Tracés repris de l'interface réelle pour que le lecteur reconnaisse. */
export const STEP_ICONS = {
  pin: (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor"
         strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 17v5" />
      <path d="M9 3h6l-1 6 3 3v2H7v-2l3-3-1-6Z" />
    </svg>
  ),
};

/* LE GUIDE NOMME LES MODULES DE LA PERSONNE (décision d'Olivier, 2026-09-24).
   Le parcours « L'essentiel » cite les modules de la plateforme, et c'est très
   bien : on peut parler de ce qui existe. Mais il énumérait CINQ noms en dur,
   si bien qu'un client qui n'en a qu'un lisait la carte complète sans savoir
   lesquels étaient les siens. Le marqueur `{modules}` porte donc la liste de
   ses modules à lui, tirée du menu déjà filtré par les droits. */
/** Texte d'une étape : marqueurs `{modules}` puis `{icon}` résolus. */
export function renderStepText(step, vars = {}) {
  const raw = vars.modules
    ? step.text.replace('{modules}', vars.modules)
    : step.text.replace(/\s*Les vôtres\s*:\s*\{modules\}\./, '');
  step = raw === step.text ? step : { ...step, text: raw };
  const icon = step.icon && STEP_ICONS[step.icon];
  if (!icon || !step.text.includes('{icon}')) return step.text;
  const [before, after] = step.text.split('{icon}');
  return (
    <>
      {before}
      <span className="gd-inline-ic">{icon}</span>
      {after}
    </>
  );
}

const PAD = 5;          // respiration autour de la cible
const BUBBLE_W = 340;   // largeur de la bulle (voir .gd-bubble)
const MARGIN = 12;      // marge minimale aux bords de la fenêtre

function rectOf(el) {
  const r = el.getBoundingClientRect();
  return { top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 };
}

/* Place la bulle ET dit de quel côté elle pointe : une flèche relie le texte
   à l'élément mis en lumière (2026-09-16), sans quoi on lit une bulle sans
   savoir ce qu'elle désigne. */
function bubblePlacement(rect) {
  if (!rect) return { style: { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }, side: null };
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // Cible étroite et haute (le rail) : bulle à sa droite, centrée.
  if (rect.height > 160 && rect.left + rect.width + BUBBLE_W + 24 < vw) {
    const top = Math.max(MARGIN, Math.min(rect.top + rect.height / 2 - 90, vh - 220));
    return {
      style: { top, left: rect.left + rect.width + PAD + 14 },
      side: 'left',
      offset: Math.max(16, Math.min(rect.top + rect.height / 2 - top, 170)),
    };
  }
  const left = Math.max(MARGIN, Math.min(rect.left, vw - BUBBLE_W - MARGIN));
  const offset = Math.max(18, Math.min(rect.left + rect.width / 2 - left, BUBBLE_W - 18));
  // Sous la cible si la place le permet, sinon au-dessus.
  if (rect.top + rect.height + PAD + 200 < vh) {
    return { style: { top: rect.top + rect.height + PAD + 12, left }, side: 'top', offset };
  }
  return { style: { bottom: vh - rect.top + PAD + 12, left }, side: 'bottom', offset };
}

/* Repères du mode Guide (2026-09-16) : l'aide est posée SUR les éléments,
   pas dans une zone à part. Allumé, chaque geste de la page porte une
   pastille numérotée près de l'élément par lequel il commence, et la fiche
   liée une pastille livre près du titre. Cliquer une pastille lit le geste
   étape par étape, halo et bulle, SANS assombrir l'écran : on lit et on fait
   dans le même mouvement. Éteint, rien ne reste.

   Une étape peut naviguer d'abord (pageKey), cliquer un onglet (click), puis
   viser un élément (target). Sans cible trouvée, la bulle se centre. */
/* `controls` : la bulle porte ses boutons seulement quand le panneau Savoirs
   est FERMÉ (lecture depuis un repère). Panneau ouvert, il est la
   télécommande, la bulle ne fait que montrer (2026-09-16) : un seul texte,
   sur l'écran, et une seule rangée de commandes. */
/* Deux jeux de repères sont les mêmes si chacun vise le même élément au même
   endroit : comparer les objets ne dirait rien, ils sont reconstruits. */
function samePins(a, b) {
  if (a.length !== b.length) return false;
  return a.every((p, i) => {
    const q = b[i];
    return p.key === q.key && p.top === q.top && p.left === q.left && p.n === q.n;
  });
}

export default function GuideOverlay({ setPage, onOpenFiche, nav, controls = true }) {
  const { t } = useTranslation();
  /* Calculé au rendu, jamais posé en état : `nav` est déjà mémoïsé par `App`,
     et une liste dérivée qui entrerait dans un effet rejouerait celui-ci à
     chaque rendu (la boucle du 2026-09-18). */
  const myModules = useMemo(() => (nav || [])
    .filter((n) => n.type === 'band' && !n.adminOnly)
    .map((n) => (n.labelKey ? t(n.labelKey) : n.label))
    .filter(Boolean)
    .join(', '), [nav, t]);
  const g = useGuide();
  /* MÉMOÏSÉ, ET CE N'EST PAS UN DÉTAIL (2026-09-18) : `helpForPage` FILTRE
     trois listes, elle rend donc de NOUVEAUX tableaux à chaque appel. Sans ce
     useMemo, `measure` changeait d'identité à chaque rendu, l'effet qui la
     surveille se rejouait, il appelait `measure`, qui posait un nouveau tableau
     de repères, ce qui rendait la page... en boucle, sans fin et sans erreur,
     mode Guide ÉTEINT compris, sur tous les écrans, ce composant étant monté
     une fois pour toutes par `App`. Safari finissait par recharger l'onglet
     (« cette page consommait beaucoup d'énergie »). Le panneau Savoirs
     mémoïsait déjà le sien. */
  const { topics, fiches } = useMemo(() => helpForPage(g.page), [g.page]);
  const topic = g.topic ? TOPICS.find((tp) => tp.id === g.topic) : null;
  const step = topic ? topic.steps[g.step] : null;

  const [pins, setPins] = useState([]);           // [{ key, kind, n, top, left, topicId, ficheId }]
  const [rect, setRect] = useState(null);         // halo de l'étape courante
  const [searching, setSearching] = useState(false);
  const [ficheOpen, setFicheOpen] = useState(null); // fiche lue depuis sa pastille
  const clicked = useRef(null);                    // étape dont le `click` a été joué

  /* Mesure des repères : un par geste dont la première cible est à l'écran,
     un pour la fiche liée, ancré au titre ou, sur un hub à onglets qui n'en
     affiche pas, à la ligne des onglets. */
  const measure = useCallback(() => {
    if (!g.on) { setPins([]); return; }
    const out = [];
    topics.forEach((tp, i) => {
      const first = tp.steps.find((s) => s.target);
      const el = first ? findTarget(first.target) : null;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (!r.width && !r.height) return;
      out.push({ key: tp.id, kind: 'topic', n: i + 1, top: r.top - 9, left: r.right - 9, topicId: tp.id });
    });
    if (fiches.length) {
      const el = findTarget('page-title') || document.querySelector('.tabs__list');
      if (el) {
        const r = el.getBoundingClientRect();
        out.push({ key: 'fiche', kind: 'fiche', top: r.top - 9, left: r.right + 4, ficheId: fiches[0].id });
      }
    }
    /* On ne repose les repères que s'ils ont BOUGÉ : le mesureur tourne toutes
       les 400 ms tant que le Guide est allumé, et un tableau neuf à chaque tour
       rendrait la page deux fois par seconde pour redessiner la même chose. */
    setPins((prev) => (samePins(prev, out) ? prev : out));
  }, [g.on, topics, fiches]);

  useEffect(() => {
    measure();
    if (!g.on) return undefined;
    const id = window.setInterval(measure, 400);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [measure, g.on]);

  /* Étape courante : naviguer, cliquer, puis chercher la cible (l'écran peut
     mettre quelques instants à se rendre). */
  const locate = useCallback(() => {
    const el = step && step.target ? findTarget(step.target) : null;
    if (!el) return false;
    el.scrollIntoView({ block: 'nearest' });
    setRect(rectOf(el));
    return true;
  }, [step]);

  useEffect(() => {
    if (!step) { setRect(null); setSearching(false); return undefined; }
    let cancelled = false;
    let tries = 0;
    setSearching(true);
    setFicheOpen(null);
    if (step.pageKey && setPage && step.pageKey !== g.page) setPage(step.pageKey);
    if (step.openPanel) {
      window.dispatchEvent(new CustomEvent('palestre:tour-panel', { detail: { open: true } }));
    }
    const stepKey = `${g.topic}:${g.step}`;
    function attempt() {
      if (cancelled) return;
      if (step.click && clicked.current !== stepKey) {
        const el = findTarget(step.click);
        if (el) { clicked.current = stepKey; el.click(); }
        else if (tries < 20) { tries += 1; setTimeout(attempt, 150); return; }
      }
      if (locate()) {
        setSearching(false);
        // Recadrer une fois la transition d'ouverture éventuelle terminée.
        setTimeout(() => { if (!cancelled) locate(); }, 420);
        return;
      }
      if (!step.target || tries >= 20) { setRect(null); setSearching(false); return; }
      tries += 1;
      setTimeout(attempt, 150);
    }
    attempt();
    return () => {
      cancelled = true;
      if (step.openPanel) {
        window.dispatchEvent(new CustomEvent('palestre:tour-panel', { detail: { open: false } }));
      }
    };
  }, [g.topic, g.step, step, setPage, locate]);  // eslint-disable-line

  // La fenêtre bouge : re-mesurer la cible courante.
  useEffect(() => {
    if (!step) return undefined;
    const onMove = () => { locate(); };
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => { window.removeEventListener('resize', onMove); window.removeEventListener('scroll', onMove, true); };
  }, [step, locate]);

  /* Clavier : Échap quitte ; pendant une lecture, → ou Entrée avance, ←
     recule, sans toucher la souris. Rien n'est capté depuis un champ. */
  useEffect(() => {
    if (!g.on) return undefined;
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || '';
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(tag) || (e.target && e.target.isContentEditable);
      if (e.key === 'Escape') {
        if (g.topic) guide.end();
        else if (ficheOpen) setFicheOpen(null);
        else guide.off();
        return;
      }
      if (!g.topic || typing) return;
      const total = (TOPICS.find((tp) => tp.id === g.topic) || { steps: [] }).steps.length;
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault();
        if (g.step >= total - 1) { setDone(g.topic, true); guide.end(); } else guide.goto(g.step + 1);
      } else if (e.key === 'ArrowLeft' && g.step > 0) {
        e.preventDefault();
        guide.goto(g.step - 1);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [g.on, g.topic, g.step, ficheOpen]);

  if (!g.on) return null;

  const last = topic ? g.step === topic.steps.length - 1 : false;
  const finish = () => { if (topic) setDone(topic.id, true); guide.end(); };
  const fiche = ficheOpen ? fiches.find((f) => f.id === ficheOpen) : null;

  return createPortal(
    <div className="gd" aria-live="polite">
      {pins.map((p) => (
        <button
          key={p.key} type="button"
          className={`gd-pin gd-pin--${p.kind}${g.topic === p.topicId ? ' is-active' : ''}`}
          style={{ top: p.top, left: p.left }}
          title={p.kind === 'fiche' ? t('common:guide.linkedFiche') : (topics.find((tp) => tp.id === p.topicId) || {}).title}
          onClick={() => {
            if (p.kind === 'fiche') { guide.end(); setFicheOpen(p.ficheId); }
            else { setFicheOpen(null); guide.start(p.topicId); }
          }}
        >
          {p.kind === 'fiche' ? <BookOpen size={11} strokeWidth={2} aria-hidden="true" /> : p.n}
        </button>
      ))}

      {topic && rect && !searching && (
        <div className="gd-halo" style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }} />
      )}

      {topic && !searching && (() => {
        const place = bubblePlacement(rect);
        return (
        <div className={`gd-bubble gd-bubble--step${place.side ? ` gd-arrow--${place.side}` : ''}`}
             role="dialog" style={{ ...place.style, '--arrow': `${place.offset || 24}px` }}>
          <div className="gd-bubble-head">
            <span className="gd-num" aria-hidden="true">{g.step + 1}</span>
            <span className="gd-bubble-title">{topic.title}</span>
            <button type="button" className="gd-quit" onClick={() => guide.end()}
                    aria-label={t('common:onboarding.coach.quit')}>
              <X size={14} strokeWidth={2} aria-hidden="true" />
            </button>
          </div>
          <p className="gd-text">{renderStepText(step, { modules: myModules })}</p>
          <div className="gd-actions">
            {/* Les étapes en pastilles : on voit d'un coup d'œil où l'on en
                est et combien il en reste, ce qu'un « 3 / 3 » ne montre pas. */}
            <span className="gd-dots" aria-hidden="true">
              {topic.steps.map((_, i) => (
                <span key={i} className={`gd-dot${i === g.step ? ' is-now' : ''}${i < g.step ? ' is-past' : ''}`} />
              ))}
            </span>
            <span className="gd-count">
              {t('common:guide.stepOf', { index: g.step + 1, total: topic.steps.length })}
              {!controls && <span className="gd-keys"> · {t('common:guide.keys')}</span>}
            </span>
            {controls && g.step > 0 && (
              <button type="button" className="gd-btn" onClick={() => guide.goto(g.step - 1)}>
                ‹ {t('common:onboarding.coach.prev')}
              </button>
            )}
            {controls && (
              <button type="button" className="gd-btn gd-btn--primary"
                      onClick={() => (last ? finish() : guide.goto(g.step + 1))}>
                {last ? t('common:onboarding.coach.finish') : `${t('common:onboarding.coach.next')} ›`}
              </button>
            )}
          </div>
        </div>
        );
      })()}

      {fiche && !topic && (
        <div className="gd-bubble" role="dialog"
             style={bubblePlacement(pins.find((p) => p.kind === 'fiche') ? {
               top: pins.find((p) => p.kind === 'fiche').top, left: pins.find((p) => p.kind === 'fiche').left - 40, width: 60, height: 20,
             } : null).style}>
          <div className="gd-bubble-head">
            <span className="gd-bubble-title">{t('common:guide.linkedFiche')}</span>
            <button type="button" className="gd-quit" onClick={() => setFicheOpen(null)} aria-label={t('common:actions.close')}>
              <X size={13} strokeWidth={2} aria-hidden="true" />
            </button>
          </div>
          <p className="gd-text"><b>{fiche.title}</b><br />{fiche.summary}</p>
          <div className="gd-actions">
            <button type="button" className="gd-btn gd-btn--primary"
                    onClick={() => { setFicheOpen(null); onOpenFiche && onOpenFiche(fiche.id); }}>
              {t('common:guide.readFiche')} ›
            </button>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}
